import type { Labelled } from './common.js';

/**
 * أدوار الفريق داخل الشركة الواحدة.
 */

export const MEMBERSHIP_ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'SALES', 'EMPLOYEE'] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export interface MembershipRoleDef extends Labelled<MembershipRole> {
  /**
   * الرتبة. الأعلى يشمل صلاحيات ما دونه، وهي نفس الأرقام التي يفحصها
   * حارس الأدوار في الخادم — تُقرأ من هنا فلا يفترقان.
   */
  rank: number;
  description: string;
  /** لا يُمنح بالدعوة: المالك واحد يُنشأ مع الشركة أو يُنقل إليه المُلك. */
  assignable: boolean;
}

export const MEMBERSHIP_ROLE_DEFS: readonly MembershipRoleDef[] = [
  {
    key: 'OWNER',
    label: 'مالك النظام',
    kind: 'brand',
    rank: 5,
    description: 'صلاحية كاملة على كل شيء، بما في ذلك الاشتراك ونقل الملكية.',
    assignable: false,
  },
  {
    key: 'ADMIN',
    label: 'مدير النظام',
    kind: 'info',
    rank: 4,
    description: 'يدير الفريق والإعدادات وكل بيانات العمل، وتُضيّق صلاحياتُه بملفّ.',
    assignable: true,
  },
  /*
   * «مدير» و«مبيعات» أُدمِجا في الأدوار الثلاثة: مدير→مدير النظام،
   * مبيعات→موظّف. يبقيان في التعداد لحمايةِ نقاطِ الخادمِ القائمةِ وبيانات
   * الأعضاءِ القديمة، لكنّهما لا يُمنحان بعد اليوم (assignable=false) — فلا
   * يظهران في أيّ قائمةِ اختيار. والتضييقُ الدقيقُ صار من ملفّ الصلاحيات.
   */
  {
    key: 'MANAGER',
    label: 'مدير (قديم)',
    kind: 'info',
    rank: 3,
    description: 'دورٌ قديمٌ أُدمج في «مدير النظام».',
    assignable: false,
  },
  {
    key: 'SALES',
    label: 'مبيعات (قديم)',
    kind: 'success',
    rank: 2,
    description: 'دورٌ قديمٌ أُدمج في «الموظف».',
    assignable: false,
  },
  {
    key: 'EMPLOYEE',
    label: 'موظف',
    kind: 'neutral',
    rank: 1,
    description: 'وصولٌ أساسيّ، وتُوسَّع صلاحياتُه أو تُضيَّق بملفّ.',
    assignable: true,
  },
] as const;

export const MEMBERSHIP_ROLE_MAP = Object.fromEntries(
  MEMBERSHIP_ROLE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<MembershipRole, MembershipRoleDef>>;

export const ROLE_RANK: Readonly<Record<MembershipRole, number>> = Object.fromEntries(
  MEMBERSHIP_ROLE_DEFS.map((d) => [d.key, d.rank]),
) as Record<MembershipRole, number>;

/**
 * الأدوار التي تُمنح بالدعوة أو بالتعديل — مدير النظام والموظف.
 *
 * المالكُ ليس منها: شخصٌ واحدٌ يُنشأ مع الاشتراكِ وحده، ولا يُمنح لأحدٍ
 * بعده إلا بنقلِ الملكية. والقديمان (مدير، مبيعات) أُسقطا بالدمج.
 */
export const ASSIGNABLE_ROLES = MEMBERSHIP_ROLE_DEFS.filter((d) => d.assignable).map(
  (d) => d.key,
) as readonly MembershipRole[];

export function roleAtLeast(role: MembershipRole, floor: MembershipRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[floor];
}

/* ==========================================================================
   حالة العضوية
   ========================================================================== */

export const MEMBERSHIP_STATUSES = ['ACTIVE', 'INVITED', 'SUSPENDED'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const MEMBERSHIP_STATUS_DEFS: readonly Labelled<MembershipStatus>[] = [
  { key: 'ACTIVE', label: 'نشط', kind: 'success' },
  { key: 'INVITED', label: 'مدعو', kind: 'info' },
  { key: 'SUSPENDED', label: 'موقوف', kind: 'danger' },
] as const;

export const MEMBERSHIP_STATUS_MAP = Object.fromEntries(
  MEMBERSHIP_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<MembershipStatus, Labelled<MembershipStatus>>>;

/* ==========================================================================
   الدعوات
   ========================================================================== */

/**
 * صلاحية الدعوة.
 *
 * أسبوع: أطول من أن يفوت من هو في إجازة، وأقصر من أن يبقى رابط دخول صالحًا
 * في بريد منسيّ إلى ما لا نهاية.
 */
export const INVITATION_TTL_DAYS = 7;

export function invitationExpiry(from: Date = new Date()): Date {
  return new Date(from.getTime() + INVITATION_TTL_DAYS * 86_400_000);
}

export function invitationExpired(expiresAt: Date | string): boolean {
  return new Date(expiresAt).getTime() <= Date.now();
}
